import * as THREE from "three";

export type LithophaneShape = "flat" | "curved" | "cylinder";

export interface LithophaneSettings {
  shape: LithophaneShape;
  widthMm: number;
  heightMm: number;
  diameterMm: number;
  curveAngleDeg: number;
  minThicknessMm: number;
  maxThicknessMm: number;
  samplePitchMm: number;
  borderMm: number;
  brightness: number;
  contrast: number;
  gamma: number;
  invert: boolean;
  maxTriangles: number;
}

export interface LithophaneStats {
  columns: number;
  rows: number;
  vertices: number;
  triangles: number;
  effectivePitchMm: number;
  mappedWidthMm: number;
  heightMm: number;
  estimatedBinaryStlMb: number;
}

export interface LithophaneResult {
  geometry: THREE.BufferGeometry;
  processedPreviewUrl: string;
  stats: LithophaneStats;
}

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value));

function adjustedLuminance(
  red: number,
  green: number,
  blue: number,
  brightness: number,
  contrast: number,
  gamma: number,
): number {
  let value = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  value += brightness / 100;
  value = (value - 0.5) * (1 + (contrast / 100) * 1.8) + 0.5;
  value = clamp(value, 0, 1);
  return Math.pow(value, clamp(gamma, 0.2, 4));
}

function calculateGrid(
  mappedWidthMm: number,
  heightMm: number,
  samplePitchMm: number,
  maxTriangles: number,
  cylinder: boolean,
): { columns: number; rows: number; effectivePitchMm: number } {
  let columns = Math.max(
    cylinder ? 24 : 12,
    Math.round(mappedWidthMm / samplePitchMm) + (cylinder ? 0 : 1),
  );
  let rows = Math.max(12, Math.round(heightMm / samplePitchMm) + 1);

  const estimateFrontTriangles = () =>
    2 * (cylinder ? columns : columns - 1) * (rows - 1);

  if (estimateFrontTriangles() > maxTriangles) {
    const scale = Math.sqrt(maxTriangles / estimateFrontTriangles());
    columns = Math.max(cylinder ? 24 : 12, Math.floor(columns * scale));
    rows = Math.max(12, Math.floor(rows * scale));
  }

  const effectivePitchMm = cylinder
    ? mappedWidthMm / columns
    : mappedWidthMm / Math.max(1, columns - 1);

  return { columns, rows, effectivePitchMm };
}

function createProcessedImage(
  image: HTMLImageElement,
  columns: number,
  rows: number,
  settings: LithophaneSettings,
): { luminance: Float32Array; previewUrl: string } {
  const canvas = document.createElement("canvas");
  canvas.width = columns;
  canvas.height = rows;

  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    throw new Error("Your browser could not create the image-processing canvas.");
  }

  context.drawImage(image, 0, 0, columns, rows);
  const imageData = context.getImageData(0, 0, columns, rows);
  const luminance = new Float32Array(columns * rows);

  for (let index = 0; index < columns * rows; index += 1) {
    const dataIndex = index * 4;
    const value = adjustedLuminance(
      imageData.data[dataIndex],
      imageData.data[dataIndex + 1],
      imageData.data[dataIndex + 2],
      settings.brightness,
      settings.contrast,
      settings.gamma,
    );

    luminance[index] = value;
    const grayscale = Math.round(value * 255);
    imageData.data[dataIndex] = grayscale;
    imageData.data[dataIndex + 1] = grayscale;
    imageData.data[dataIndex + 2] = grayscale;
    imageData.data[dataIndex + 3] = 255;
  }

  context.putImageData(imageData, 0, 0);
  return {
    luminance,
    previewUrl: canvas.toDataURL("image/png"),
  };
}

export function buildLithophane(
  image: HTMLImageElement,
  rawSettings: LithophaneSettings,
): LithophaneResult {
  const settings: LithophaneSettings = {
    ...rawSettings,
    widthMm: clamp(rawSettings.widthMm, 20, 500),
    heightMm: clamp(rawSettings.heightMm, 20, 500),
    diameterMm: clamp(rawSettings.diameterMm, 20, 300),
    curveAngleDeg: clamp(rawSettings.curveAngleDeg, 15, 330),
    minThicknessMm: clamp(rawSettings.minThicknessMm, 0.4, 4),
    maxThicknessMm: clamp(rawSettings.maxThicknessMm, 0.8, 8),
    samplePitchMm: clamp(rawSettings.samplePitchMm, 0.2, 2),
    borderMm: clamp(rawSettings.borderMm, 0, 20),
    maxTriangles: clamp(rawSettings.maxTriangles, 50_000, 1_000_000),
  };

  if (settings.maxThicknessMm <= settings.minThicknessMm) {
    throw new Error("Maximum thickness must be greater than minimum thickness.");
  }

  const isCylinder = settings.shape === "cylinder";
  const mappedWidthMm = isCylinder
    ? Math.PI * settings.diameterMm
    : settings.widthMm;

  const { columns, rows, effectivePitchMm } = calculateGrid(
    mappedWidthMm,
    settings.heightMm,
    settings.samplePitchMm,
    settings.maxTriangles,
    isCylinder,
  );

  const { luminance, previewUrl } = createProcessedImage(
    image,
    columns,
    rows,
    settings,
  );

  const vertexCount = columns * rows * 2;
  const positions = new Float32Array(vertexCount * 3);
  const colors = new Float32Array(vertexCount * 3);
  const indices: number[] = [];

  const outerIndex = (row: number, column: number) => row * columns + column;
  const innerIndex = (row: number, column: number) =>
    columns * rows + row * columns + column;

  const heightStep = settings.heightMm / Math.max(1, rows - 1);
  const widthStep = isCylinder
    ? mappedWidthMm / columns
    : mappedWidthMm / Math.max(1, columns - 1);

  const curveAngleRadians = THREE.MathUtils.degToRad(settings.curveAngleDeg);
  const curvedBaseRadius =
    settings.shape === "curved"
      ? settings.widthMm / Math.max(curveAngleRadians, 0.001)
      : 0;
  const cylinderBaseRadius = settings.diameterMm / 2;

  const borderColumns = Math.round(settings.borderMm / widthStep);
  const borderRows = Math.round(settings.borderMm / heightStep);

  const writeVertex = (
    vertexIndex: number,
    x: number,
    y: number,
    z: number,
    shade: number,
  ) => {
    const positionOffset = vertexIndex * 3;
    positions[positionOffset] = x;
    positions[positionOffset + 1] = y;
    positions[positionOffset + 2] = z;

    const colorOffset = vertexIndex * 3;
    colors[colorOffset] = shade;
    colors[colorOffset + 1] = shade;
    colors[colorOffset + 2] = shade;
  };

  for (let row = 0; row < rows; row += 1) {
    const y = settings.heightMm / 2 - row * heightStep;

    for (let column = 0; column < columns; column += 1) {
      const luminanceIndex = row * columns + column;
      let value = luminance[luminanceIndex];

      const onVerticalBorder = row < borderRows || row >= rows - borderRows;
      const onHorizontalBorder =
        !isCylinder &&
        (column < borderColumns || column >= columns - borderColumns);

      if (settings.borderMm > 0 && (onVerticalBorder || onHorizontalBorder)) {
        value = settings.invert ? 1 : 0;
      }

      const normalizedThickness = settings.invert ? value : 1 - value;
      const thickness =
        settings.minThicknessMm +
        normalizedThickness *
          (settings.maxThicknessMm - settings.minThicknessMm);

      const previewShade = clamp(
        1.03 - normalizedThickness * 0.88,
        0.12,
        1,
      );

      let outerX = 0;
      let outerZ = 0;
      let innerX = 0;
      let innerZ = 0;

      if (settings.shape === "flat") {
        const x = -settings.widthMm / 2 + column * widthStep;
        outerX = x;
        innerX = x;
        outerZ = thickness;
        innerZ = 0;
      } else {
        const angle =
          settings.shape === "cylinder"
            ? -Math.PI + (column / columns) * Math.PI * 2
            : -curveAngleRadians / 2 +
              (column / Math.max(1, columns - 1)) * curveAngleRadians;

        const baseRadius =
          settings.shape === "cylinder"
            ? cylinderBaseRadius
            : curvedBaseRadius;
        const outerRadius = baseRadius + thickness;

        outerX = Math.sin(angle) * outerRadius;
        outerZ = Math.cos(angle) * outerRadius;
        innerX = Math.sin(angle) * baseRadius;
        innerZ = Math.cos(angle) * baseRadius;
      }

      writeVertex(
        outerIndex(row, column),
        outerX,
        y,
        outerZ,
        previewShade,
      );
      writeVertex(innerIndex(row, column), innerX, y, innerZ, 0.16);
    }
  }

  const segmentCount = isCylinder ? columns : columns - 1;

  // Detailed image-facing surface.
  for (let row = 0; row < rows - 1; row += 1) {
    for (let column = 0; column < segmentCount; column += 1) {
      const nextColumn = isCylinder ? (column + 1) % columns : column + 1;
      const topLeft = outerIndex(row, column);
      const topRight = outerIndex(row, nextColumn);
      const bottomLeft = outerIndex(row + 1, column);
      const bottomRight = outerIndex(row + 1, nextColumn);

      indices.push(topLeft, bottomLeft, bottomRight);
      indices.push(topLeft, bottomRight, topRight);
    }
  }

  const frontIndexCount = indices.length;

  // Low-triangle constant-thickness rear/inner surface.
  if (settings.shape === "flat") {
    const topLeft = innerIndex(0, 0);
    const topRight = innerIndex(0, columns - 1);
    const bottomLeft = innerIndex(rows - 1, 0);
    const bottomRight = innerIndex(rows - 1, columns - 1);
    indices.push(topLeft, topRight, bottomRight);
    indices.push(topLeft, bottomRight, bottomLeft);
  } else {
    for (let column = 0; column < segmentCount; column += 1) {
      const nextColumn = isCylinder ? (column + 1) % columns : column + 1;
      const topLeft = innerIndex(0, column);
      const topRight = innerIndex(0, nextColumn);
      const bottomLeft = innerIndex(rows - 1, column);
      const bottomRight = innerIndex(rows - 1, nextColumn);
      indices.push(topLeft, topRight, bottomRight);
      indices.push(topLeft, bottomRight, bottomLeft);
    }
  }

  // Top and bottom closure.
  for (let column = 0; column < segmentCount; column += 1) {
    const nextColumn = isCylinder ? (column + 1) % columns : column + 1;

    const innerTop = innerIndex(0, column);
    const innerTopNext = innerIndex(0, nextColumn);
    const outerTop = outerIndex(0, column);
    const outerTopNext = outerIndex(0, nextColumn);

    indices.push(innerTop, outerTop, outerTopNext);
    indices.push(innerTop, outerTopNext, innerTopNext);

    const innerBottom = innerIndex(rows - 1, column);
    const innerBottomNext = innerIndex(rows - 1, nextColumn);
    const outerBottom = outerIndex(rows - 1, column);
    const outerBottomNext = outerIndex(rows - 1, nextColumn);

    indices.push(innerBottom, innerBottomNext, outerBottomNext);
    indices.push(innerBottom, outerBottomNext, outerBottom);
  }

  // Curved and flat panels require end caps; a full cylinder has no open ends.
  if (!isCylinder) {
    for (let row = 0; row < rows - 1; row += 1) {
      const innerLeftTop = innerIndex(row, 0);
      const innerLeftBottom = innerIndex(row + 1, 0);
      const outerLeftTop = outerIndex(row, 0);
      const outerLeftBottom = outerIndex(row + 1, 0);

      indices.push(innerLeftTop, innerLeftBottom, outerLeftBottom);
      indices.push(innerLeftTop, outerLeftBottom, outerLeftTop);

      const lastColumn = columns - 1;
      const innerRightTop = innerIndex(row, lastColumn);
      const innerRightBottom = innerIndex(row + 1, lastColumn);
      const outerRightTop = outerIndex(row, lastColumn);
      const outerRightBottom = outerIndex(row + 1, lastColumn);

      indices.push(innerRightTop, outerRightTop, outerRightBottom);
      indices.push(innerRightTop, outerRightBottom, innerRightBottom);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.clearGroups();
  geometry.addGroup(0, frontIndexCount, 0);
  geometry.addGroup(frontIndexCount, indices.length - frontIndexCount, 1);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();

  const triangles = indices.length / 3;

  return {
    geometry,
    processedPreviewUrl: previewUrl,
    stats: {
      columns,
      rows,
      vertices: vertexCount,
      triangles,
      effectivePitchMm,
      mappedWidthMm,
      heightMm: settings.heightMm,
      estimatedBinaryStlMb: (84 + triangles * 50) / 1_000_000,
    },
  };
}
